import { CarouselItemVariants, CarouselVariants } from 'constants/carousel';
import { IMGS_PATH } from 'constants/image';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import LazyLoad from 'react-lazyload';
import Carousel from 'react-multi-carousel';
import CarouselContainer from 'components/Shared/Carousel/CarouselContainer';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import Image from 'components/ui/Image';
import tailwind from 'utils/breakpoints';
import { useTranslation } from 'hooks/useTranslation';
import { useVerticalSwipePreventer } from 'hooks/useVerticalSwipePreventer';
import baStyles from './BeforeAfterPhotos.module.scss';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

type Props = {
  beforeAfterPhotos: APIBeforeAfterPhoto[];
  getPhotos: (
    offset?: number,
    limit?: number,
    all?: boolean,
  ) => Promise<Record<string, { baphotos: APIBeforeAfterPhoto[] }>>;
  slug: string;
  totalCount?: string | number;
  type: string;
};

const BeforeAfterPhotos = ({
  beforeAfterPhotos,
  getPhotos,
  slug,
  type,
  totalCount = 0,
}: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<Carousel | null>(null);
  const { locale, t } = useTranslation();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [, setIsMobile] = useState(false);
  const [oldSlide, setOldSlide] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [hasAllImagesLoaded, setHasAllImagesLoaded] = useState(false);
  const [photos, setPhotos] = useState<APIBeforeAfterPhoto[]>(
    beforeAfterPhotos || [],
  );
  useVerticalSwipePreventer(containerRef);

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
  }, []);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.goToSlide(activeSlide);
    }
  }, [activeSlide]);

  return (
    <div className="-mx-6 lg:mx-0 w-full" ref={containerRef}>
      <LazyLoad once>
        <div className="fluid-lazyload md:px-12 relative">
          <Carousel
            ref={sliderRef}
            responsive={{
              other: {
                breakpoint: { min: 768, max: 4000 },
                items: 1,
                partialVisibilityGutter: 250,
              },
              mobile: {
                breakpoint: { min: 0, max: 768 },
                items: 1,
                partialVisibilityGutter: 40,
              },
            }}
            arrows={false}
            partialVisible
            removeArrowOnDeviceType={['mobile']}
            containerClass={`flex relative items-center lg:overflow-hidden px-6 md:pl-8 lg:pl-0 ${
              baStyles.baCarousel
            } ${activeSlide !== 0 ? baStyles.showBefore : ''} ${
              activeSlide !== photos.length - 1 ? baStyles.showAfter : ''
            }`}
            sliderClass="flex relative flex-row list-none p-0 m-0"
            itemClass={`${baStyles.carouselItem} pb-2`}
            beforeChange={() => {
              if (
                activeSlide > oldSlide &&
                !hasAllImagesLoaded &&
                activeSlide === photos.length - 2
              ) {
                void getPhotos(photos.length).then((result) => {
                  const updatePhotos = [...photos, ...result.items.baphotos];
                  setPhotos(updatePhotos);
                  if (result.items.baphotos.length <= 1) {
                    setHasAllImagesLoaded(true);
                  }
                });
              }
            }}
          >
            {photos.length >= 1 &&
              photos.map((photo, i: number) => {
                if (!photo.beforemedia || !photo.aftermedia) {
                  return null;
                }

                const { aftermedia, beforemedia, beforeafterphoto } = photo;

                return (
                  <div
                    key={`photo_${i}`}
                    onClick={() => {
                      setIsGalleryOpen(true);
                    }}
                  >
                    <div className="slide border border-silver-light overflow-hidden rounded-lg shrink-0 mx-2 cursor-pointer">
                      <div className="flex">
                        <div className="w-1/2 h-40 lg:h-58 flex items-center justify-center fluid-lazyload relative">
                          {locale === 'de' &&
                          beforeafterphoto?.omit_before_picture ? (
                            <Image
                              src={`${IMGS_PATH.PUBLIC}/before-after-photo-de-placeholder.png`}
                              alt="Placeholder DE - Before/After"
                              className="self-center w-full"
                              fill
                            />
                          ) : (
                            <Image
                              src={beforemedia.url as string}
                              alt={beforemedia.name}
                              className="self-center h-full w-full object-cover"
                              contentful
                              fill
                            />
                          )}
                        </div>
                        <div className="w-1/2 h-40 lg:h-58 flex items-center justify-center fluid-lazyload relative">
                          <Image
                            src={aftermedia.url as string}
                            alt={aftermedia.name}
                            className="self-center h-full w-full object-cover"
                            contentful
                            fill
                          />
                        </div>
                      </div>
                      <div
                        className={clsx(
                          baStyles.label,
                          'text-primary bg-white text-base py-2 pb-3 px-4 border-t relative',
                        )}
                        dangerouslySetInnerHTML={{
                          __html: beforeafterphoto?.description || '',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </Carousel>

          <div
            className={clsx(
              'hidden md:block w-full absolute',
              baStyles.carouselArrows,
            )}
          >
            <div className={clsx(!activeSlide && 'hidden')}>
              <CustomSliderArrow
                className={baStyles.carouselArrowLeft}
                direction="left"
                color={tailwind.theme.backgroundColor.navy}
                onClick={() => {
                  if (activeSlide > 0) {
                    setOldSlide(activeSlide);
                    setActiveSlide(activeSlide - 1);
                  }
                }}
              />
            </div>
            <div
              className={clsx(
                'right-0 absolute',
                activeSlide === photos.length - 1 && 'hidden',
              )}
            >
              <CustomSliderArrow
                direction="right"
                className={baStyles.carouselArrowRight}
                color={tailwind.theme.backgroundColor.navy}
                onClick={() => {
                  if (activeSlide < photos.length - 1) {
                    setOldSlide(activeSlide);
                    setActiveSlide(activeSlide + 1);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <p className="text-right mt-4">
          <span
            className="underline hidden lg:block cursor-pointer"
            onClick={() => {
              setIsGalleryOpen(true);
            }}
          >
            {t('doctorProfile.viewPhotos')}
          </span>
        </p>
        {isGalleryOpen && (
          <CarouselContainer
            slides={photos.filter(
              (photo) => photo.aftermedia && photo.beforemedia,
            )}
            onClose={() => {
              setIsGalleryOpen(false);
            }}
            startSlide={0}
            itemVariant={CarouselItemVariants.BEFORE_AFTER}
            variant={CarouselVariants.DEFAULT}
            slug={slug}
            pageType={type}
            totalCount={totalCount}
          />
        )}
      </LazyLoad>
    </div>
  );
};

export default BeforeAfterPhotos;
